import React from 'react';
import {connect} from 'react-redux';
import partial from 'lodash/partial';
import includes from 'lodash/includes';
import compose from 'recompose/compose';
import {bindActionCreators} from 'redux';
import {withRouter} from 'react-router-dom';
import lifecycle from 'recompose/lifecycle';
import withProps from 'recompose/withProps';
import {FormattedMessage} from 'react-intl';
import {Menu, Grid} from 'semantic-ui-react';
import setDisplayName from 'recompose/setDisplayName';
import {Route, Switch, NavLink} from 'react-router-dom';
import {getNamedPrizeFilters, getPrizeSortOptions, getPrizeDateRangeBounds} from './selectors';
import CreatePrizeButton from 'pages/PrizeCreator/CreatePrizeButton';
import {filterOptionsFactory} from 'viewOptions/factories';
import * as ViewOptionsActions from 'viewOptions/actions';
import DateRangeButton from 'components/DateRange/DateRangeButton';
import DateRangeModal from 'components/DateRange/DateRangeModal';
import * as DateRangeActions from 'components/DateRange/actions';
import {DateRangeFilterTypes} from 'components/DateRange/utils';
import {exclusivePrizeFilterGroups} from 'utils/DBUtils';
import ListPrizesFilterBar from './ListPrizesFilterBar';
import FilterOperators from 'classes/FilterOperators';
import {PermissionsFilter} from 'hoc/authorization';
import ListPrizesPage from './ListPrizesPage';
import * as ResourceApi from 'api/resources';
import * as PrizeActions from './actions';
import prizeTableConfigs from './configs';

const LockedListPrizesPage = PermissionsFilter({
  [ResourceApi.Types.Prize]: ['READ']
})(ListPrizesPage);

const mapStateToProps = state => {
  const isDateRangeModalOpen = state.dateRange.isOpen;
  const currentDateRange = getPrizeDateRangeBounds(state);
  const currentDateRangeFilterType = state.listPrizes.dateRangeFilterType;

  const namedFilters = getNamedPrizeFilters(state);
  const hasNamedFilter = name => includes(namedFilters, name);
  const canAddFilter = name => {
    const filterGroup = exclusivePrizeFilterGroups.find(group => includes(group, name)) || [];
    const filterGroupAlreadyActive = filterGroup.find(hasNamedFilter);
    return !filterGroupAlreadyActive;
  };

  return {
    resourceType: ResourceApi.Types.Prize,
    namedFilters,
    hasNamedFilter,
    canAddFilter,

    isDateRangeModalOpen,
    currentLowerBound: currentDateRange.lowerBound ? new Date(currentDateRange.lowerBound) : null,
    currentUpperBound: currentDateRange.upperBound ? new Date(currentDateRange.upperBound) : null,
    currentDateRangeFilterType
  };
};

const mapDispatchToProps = {
  addFilter: name => ViewOptionsActions.addFilterForResourceAction(ResourceApi.Types.Prize, name),
  onFilterDeselected: name => ViewOptionsActions.removeFilterForResourceAction(ResourceApi.Types.Prize, name),
  clearAllFiltersWithSortOptions: sortOptions => ViewOptionsActions.removeAllFiltersForResourceAction(ResourceApi.Types.Prize, sortOptions),

  setRawFilters: ViewOptionsActions.setRawFiltersForResourceAction,
  setDateRangeFilterType: PrizeActions.setDateRangeFilterTypeAction,
  requestPrizes: PrizeActions.requestPrizesAction,
  closeDateRangeModal: DateRangeActions.closeModal
};

export default compose(
  setDisplayName('ListPrizesNav'),
  connect(mapStateToProps, mapDispatchToProps),
  withRouter,
  withProps(props => ({
    onFilterSelected: name => {
      if (props.canAddFilter(name)) {
        props.addFilter(name);
      }
    },

    clearAllFilters: () => {
      props.clearAllFiltersWithSortOptions(props.sortOptions);
    },

    prizeTableConfig: prizeTableConfigs[props.match.params.prizeType],

    filterTypes: [DateRangeFilterTypes.LiveRange, DateRangeFilterTypes.EndDate].map(filterType => ({
      content: <FormattedMessage id={`dateRange.filterType.${filterType}`} />,
      value: filterType
    })),

    clearDateRange: () => {
      props.setRawFilters(ResourceApi.Types.Prize, []);
      props.closeDateRangeModal();
    },

    setDateRange: ({lowerBound, upperBound, filterType}) => {
      const filters = [{
        field: 'endTime',
        operator: FilterOperators.GreaterThanOrEqual,
        value: lowerBound.valueOf()
      }, {
        field: filterType === DateRangeFilterTypes.LiveRange ? 'startTime' : 'endTime',
        operator: FilterOperators.LessThanOrEqual,
        value: upperBound.valueOf()
      }];

      props.setRawFilters(ResourceApi.Types.Prize, filters);
      props.setDateRangeFilterType(filterType);
      props.closeDateRangeModal();
    }
  })),
  lifecycle({
    componentWillMount() {
      this.props.requestPrizes(this.props.prizeTableConfig.prizeType);
    },

    componentDidUpdate(prevProps, prevState) {
      const prizeTypeChanged = this.props.prizeTableConfig.prizeType !== prevProps.prizeTableConfig.prizeType;

      if (prizeTypeChanged) {
        // route has changed causing Prize `type` to change, so fetch with that filter applied.
        this.props.requestPrizes(this.props.prizeTableConfig.prizeType);
      }
    }
  })
)(props => (
  <React.Fragment>
    <Grid columns={2} className='list-prizes-nav-container'>
      <Grid.Row>
        <Grid.Column width={3} textAlign='left'>
          <Menu tabular className='list-prizes-nav'>
            <Menu.Item
              as={NavLink}
              to='/prizes/editorial'
              content={<FormattedMessage id='prizeList.nav.tab.editorialSweeps' />}
              activeClassName='active' />
            <hr />
            <Menu.Item
              as={NavLink}
              to='/prizes/sponsored'
              content={<FormattedMessage id='prizeList.nav.tab.sponsoredPrizing' />}
              activeClassName='active' />
          </Menu>
        </Grid.Column>
        <Grid.Column width={13} textAlign='right'>
          <Grid columns={3} className='list-prizes-controls'>
            <Grid.Row>
              <Grid.Column width={10} textAlign='right' className='filter-bar'>
                <ListPrizesFilterBar {...props} />
              </Grid.Column>
              <Grid.Column width={3} textAlign='center'>
                <DateRangeButton />
              </Grid.Column>
              <Grid.Column width={3} textAlign='center'>
                <CreatePrizeButton />
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Grid.Column>
      </Grid.Row>
    </Grid>
    <Switch>
      <Route exact path='/prizes/editorial' render={() => <LockedListPrizesPage {...props} />} />
      <Route exact path='/prizes/sponsored' render={() => <LockedListPrizesPage {...props} />} />
    </Switch>
    {props.isDateRangeModalOpen && (
      <DateRangeModal
        lowerBoundLabelKey='prizeList.dateRange.fromDate'
        upperBoundLabelKey='prizeList.dateRange.toDate'
        submit={props.setDateRange}
        clear={props.clearDateRange}
        filterTypes={props.filterTypes}
        close={props.closeDateRangeModal}
        isOpen={props.isDateRangeModalOpen}
        defaultLowerBound={props.currentLowerBound}
        defaultUpperBound={props.currentUpperBound}
        defaultFilterType={props.currentDateRangeFilterType} />
    )}
  </React.Fragment>
));



// WEBPACK FOOTER //
// ./public/pages/ListPrizes/ListPrizesNav.jsx